import { Link } from "react-router-dom";
import { Building2, Download, Loader2, PenTool, User } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { formatCents } from "../../../../lib/sales";
import { orderBuyer } from "../../../../lib/cardOrders";
import { isPaidLogoOrder, type LogoOrderRow as Row } from "../../../../lib/logoOrders";
import { IconBtn, StatusPill, fmtDate } from "../../../ui";
import { OrderPick, type OrderPicks } from "../orderPicks";

/**
 * Date (with the box in front of it), order, buyer, invoice, amount, status, and the 44px
 * file button. Here rather than in the list, so the head and the rows read the same
 * tracks. The first track never goes below what the box, the icon and a date need.
 */
export const LOGO_ORDER_COLS = "minmax(150px,1.05fr) minmax(0,1.3fr) minmax(0,1.5fr) .9fr .8fr .9fr 44px";

/** The order's state in one pill: paid or not, and a test payment said out loud. */
export function LogoOrderStatus({ order }: { order: Pick<Row, "status" | "test"> }) {
  const t = useT();
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      {isPaidLogoOrder(order) ? (
        <StatusPill tone="ok">{t("sales.logoOrders.statusPaid")}</StatusPill>
      ) : (
        <StatusPill tone="mut">{t("sales.logoOrders.statusPending")}</StatusPill>
      )}
      {order.test ? <StatusPill tone="wt">{t("sales.logoOrders.test")}</StatusPill> : null}
    </span>
  );
}

/** One order. The code opens the order; the invoice and the file keep their own clicks. */
export function LogoOrderRow({
  order,
  picks,
  downloading,
  onOpen,
  onDownload,
}: {
  order: Row;
  picks: OrderPicks;
  downloading: boolean;
  onOpen: (order: Row) => void;
  onDownload: (order: Row) => void;
}) {
  const t = useT();
  const Kind = order.billing?.kind === "company" ? Building2 : User;
  const buyer = orderBuyer(order);

  return (
    // A div, not a button: the row holds a link and a button of its own. The code's button
    // stretches an ::after over the row instead, and the two controls sit above it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: LOGO_ORDER_COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        <OrderPick picks={picks} row={order} paid={isPaidLogoOrder(order)} />
        <span className="crm-av crm-av-soft" style={{ width: 34, height: 34 }} aria-hidden>
          <PenTool className="size-4" />
        </span>
        <span className="crm-tnum crm-trunc text-[13px]">{fmtDate(order.createdAt)}</span>
      </span>

      <span className="min-w-0">
        <button
          type="button"
          onClick={() => onOpen(order)}
          className="crm-prow-n crm-tnum crm-trunc block max-w-full text-left after:absolute after:inset-0 after:rounded-full"
        >
          {order.code}
        </button>
        <span className="crm-prow-s crm-trunc block">{order.name}</span>
      </span>

      <span className="min-w-0">
        <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-semibold">
          <Kind className="size-3.5 flex-none" aria-hidden />
          <span className="crm-trunc">{buyer}</span>
        </span>
        {buyer !== order.email ? <span className="crm-prow-s crm-trunc block">{order.email}</span> : null}
      </span>

      <span className="min-w-0">
        {order.invoiceId ? (
          <Link to={`/sales/invoices/${order.invoiceId}`} className="crm-tnum crm-trunc relative z-[1] block text-[13px] hover:underline">
            {order.invoiceNumber || t("sales.logoOrders.invoice")}
          </Link>
        ) : (
          <span className="crm-mut text-[12.5px]">-</span>
        )}
      </span>

      <span className="crm-tnum justify-self-end text-[13.25px] font-bold">{formatCents(order.amountCents, order.currency)}</span>

      <LogoOrderStatus order={order} />

      {order.hasFile ? (
        <IconBtn
          small
          className="relative z-[1]"
          onClick={() => onDownload(order)}
          disabled={downloading}
          aria-label={t("sales.logoOrders.download")}
          title={t("sales.logoOrders.download")}
        >
          {downloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
        </IconBtn>
      ) : (
        <span />
      )}
    </div>
  );
}
